import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../firebaseConfig';
import { collection, doc, getDoc, setDoc, onSnapshot, orderBy, query, deleteDoc } from 'firebase/firestore';
import MessageModal from '../components/MessageModal';
import AddAnalysisModal from '../components/AddAnalysisModal';
import AnalysisViewer from '../components/AnalysisViewer';
import SearchAndFilterBar from '../components/SearchAndFilterBar';

const ComparisonResult = ({ myPrice, competitorPrice }) => {
    if (!competitorPrice || competitorPrice <= 0) { return null; }
    const difference = myPrice - competitorPrice;
    if (Math.abs(difference) < 0.01) { return <p className="comparison-result neutral">Identical Price</p>; }
    if (difference < 0) { return <p className="comparison-result positive">Lower by RM {Math.abs(difference).toFixed(2)}</p>; }
    return <p className="comparison-result negative">Higher by RM {difference.toFixed(2)}</p>;
};

const AnalysisSuggestion = ({ yourProduct, compData }) => {
    const myPrice = Number(yourProduct.sellingPriceMYR) || 0;
    const myCost = Number(yourProduct.perUnitCostMYR) || 0;
    const p1 = Number(compData.comp1_price) || 0;
    const p2 = Number(compData.comp2_price) || 0;
    let analysis = { title: "Awaiting Data", suggestion: "Enter competitor prices to generate an analysis.", color: "neutral" };
    const competitors = [p1, p2].filter(p => p > 0);
    if (competitors.length === 0) {
        return (
            <div className={`analysis-card ${analysis.color}`}>
                <h4>{analysis.title}</h4>
                <p>{analysis.suggestion}</p>
            </div>
        );
    }
    const lowestCompetitor = Math.min(...competitors);
    const highestCompetitor = Math.max(...competitors);
    const myProfit = myPrice - myCost;
    if (myPrice < lowestCompetitor) {
        analysis = {
            title: "Price Advantage",
            suggestion: `Your price is the most competitive. This is a strong position to attract customers while maintaining a profit of RM ${myProfit.toFixed(2)} per unit.`,
            color: "positive"
        };
    } else if (myPrice > highestCompetitor) {
        const isLoweringProfitable = lowestCompetitor > myCost;
        analysis = {
            title: "Price is High",
            suggestion: isLoweringProfitable
                ? `You are priced higher than all tracked competitors. To be more competitive, consider lowering your price towards RM ${lowestCompetitor.toFixed(2)}. This would still maintain a profit.`
                : `You are priced higher, but matching the lowest competitor at RM ${lowestCompetitor.toFixed(2)} would result in a loss. Hold your price and focus on justifying the value (e.g., quality, service).`,
            color: "negative"
        };
    } else {
         const isMatchingProfitable = lowestCompetitor > myCost;
        analysis = {
            title: "Balanced Pricing",
            suggestion: isMatchingProfitable
                ? `Your price is positioned between competitors. Monitor sales. If needed, matching the lower price of RM ${lowestCompetitor.toFixed(2)} is a viable strategy.`
                : `You are in a balanced position. Do not lower your price to match the competitor at RM ${lowestCompetitor.toFixed(2)}, as it would be unprofitable.`,
            color: "neutral"
        };
    }
    return (
        <div className={`analysis-card ${analysis.color}`}>
            <h4>Analysis & Suggestion</h4>
            <p><strong>{analysis.title}:</strong> {analysis.suggestion}</p>
        </div>
    );
};

const AnalysisEditor = ({ analysis, productDetails, onSave, onCancel }) => {
    const [compData, setCompData] = useState(analysis);
    useEffect(() => { setCompData(analysis); }, [analysis]);
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setCompData(prev => ({ ...prev, [name]: value }));
    };
    const handleSave = () => {
        const dataToSave = {
            comp1_company: compData.comp1_company || '', comp1_url: compData.comp1_url || '', comp1_price: parseFloat(compData.comp1_price) || 0,
            comp2_company: compData.comp2_company || '', comp2_url: compData.comp2_url || '', comp2_price: parseFloat(compData.comp2_price) || 0,
        };
        onSave(analysis.childId, dataToSave);
    };
    return (
        <div className="form-section" style={{marginTop: '2rem', borderTop: '4px solid var(--brand-primary)'}}>
            <h3 style={{marginTop: 0}}>Editing Analysis for: <span style={{color: 'var(--brand-primary)'}}>{analysis.childId}</span></h3>
            <div className="competitor-page-grid">
                <div className="competitor-card">
                    <h4>Your Product</h4>
                    <img src={productDetails.imageUrl} alt={productDetails.itemName} className="competitor-image-preview" />
                    <div className="spec-field-grid" style={{gridTemplateColumns: '1fr'}}>
                        <div className="spec-field"><strong>Item Name:</strong><div>{productDetails.itemName}</div></div>
                        <div className="spec-field"><strong>Your Cost (RM):</strong><div>{Number(productDetails.perUnitCostMYR).toFixed(2)}</div></div>
                        <div className="spec-field"><strong>Your Selling Price (RM):</strong><div>{Number(productDetails.sellingPriceMYR).toFixed(2)}</div></div>
                    </div>
                </div>
                <div className="competitor-card">
                    <AnalysisSuggestion yourProduct={productDetails} compData={compData} />
                    <div className="competitor-input-section">
                        <h5>Competitor 1</h5>
                        <div className="form-field"><label>Company / Platform</label><input type="text" name="comp1_company" value={compData.comp1_company} onChange={handleInputChange} placeholder="e.g., Shopee"/></div>
                        <div className="form-field price-field-with-result">
                            <label>Price (RM)</label>
                            <input type="number" step="0.01" name="comp1_price" value={compData.comp1_price} onChange={handleInputChange} placeholder="e.g., 159.90"/>
                            <ComparisonResult myPrice={productDetails.sellingPriceMYR} competitorPrice={compData.comp1_price} />
                        </div>
                        <div className="form-field full-span"><label>Product URL</label><input type="url" name="comp1_url" value={compData.comp1_url} onChange={handleInputChange} /></div>
                    </div>
                    <hr style={{margin: '1rem 0'}}/>
                    <div className="competitor-input-section">
                        <h5>Competitor 2</h5>
                        <div className="form-field"><label>Company / Platform</label><input type="text" name="comp2_company" value={compData.comp2_company} onChange={handleInputChange} placeholder="e.g., Lazada"/></div>
                        <div className="form-field price-field-with-result">
                            <label>Price (RM)</label>
                            <input type="number" step="0.01" name="comp2_price" value={compData.comp2_price} onChange={handleInputChange} placeholder="e.g., 165.00"/>
                            <ComparisonResult myPrice={productDetails.sellingPriceMYR} competitorPrice={compData.comp2_price} />
                        </div>
                        <div className="form-field full-span"><label>Product URL</label><input type="url" name="comp2_url" value={compData.comp2_url} onChange={handleInputChange} /></div>
                    </div>
                </div>
            </div>
            <div className="form-actions" style={{justifyContent: 'flex-end', marginTop: '1.5rem'}}>
                <button className="cancel-button" onClick={onCancel}>Cancel Edit</button>
                <button className="submit-button" onClick={handleSave}>Save Changes</button>
            </div>
        </div>
    );
};

const CompetitorsPage = ({ user, setView, onNavigateToAddAnalysis }) => {
    const [allAnalyses, setAllAnalyses] = useState([]);
    const [inventoryMap, setInventoryMap] = useState(new Map());
    const [loading, setLoading] = useState(true);
    const [modalState, setModalState] = useState({ message: '', onConfirm: null, showCancel: false });
    const [isViewerOpen, setIsViewerOpen] = useState(false);
    const [isEditorOpen, setIsEditorOpen] = useState(false);
    const [selectedAnalysis, setSelectedAnalysis] = useState(null);
    const [productDetails, setProductDetails] = useState(null);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        setLoading(true);
        const q = query(collection(db, "competitorAnalysis"), orderBy("lastUpdatedAt", "desc"));
        const unsubAnalyses = onSnapshot(q, (snapshot) => {
            const analysesData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data(), lastUpdatedAt: doc.data().lastUpdatedAt.toDate() }));
            setAllAnalyses(analysesData);
            setLoading(false);
        }, () => setLoading(false));

        const invQuery = query(collection(db, "inventoryItems"));
        const unsubInventory = onSnapshot(invQuery, (snapshot) => {
            const newMap = new Map();
            snapshot.forEach(doc => newMap.set(doc.id, doc.data()));
            setInventoryMap(newMap);
        });

        return () => {
            unsubAnalyses();
            unsubInventory();
        };
    }, []);

    const filteredAnalyses = useMemo(() => {
        if (!searchTerm) {
            return allAnalyses;
        }
        return allAnalyses.filter(analysis => {
            const product = inventoryMap.get(analysis.childId);
            const childIdMatch = analysis.childId.toLowerCase().includes(searchTerm.toLowerCase());
            const itemNameMatch = product ? product.itemName.toLowerCase().includes(searchTerm.toLowerCase()) : false;
            return childIdMatch || itemNameMatch;
        });
    }, [searchTerm, allAnalyses, inventoryMap]);

    const fetchProductDetails = async (childId) => {
        return inventoryMap.get(childId) || null;
    };

    const handleView = async (analysis) => {
        const details = await fetchProductDetails(analysis.childId);
        if (details) {
            setProductDetails(details);
            setSelectedAnalysis(analysis);
            setIsViewerOpen(true);
        } else {
            setModalState({ message: `Could not find product details for ${analysis.childId}`});
        }
    };
    
    const handleEdit = async (analysis) => {
        const details = await fetchProductDetails(analysis.childId);
        if (details) {
            setProductDetails(details);
            setSelectedAnalysis(analysis);
            setIsEditorOpen(true);
            setTimeout(() => window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' }), 100);
        } else {
            setModalState({ message: `Could not find product details for ${analysis.childId}`});
        }
    };

    const handleDelete = (analysisId) => {
        setModalState({
            message: `Are you sure you want to delete this analysis for ${analysisId}?`,
            showCancel: true,
            onConfirm: async () => {
                await deleteDoc(doc(db, "competitorAnalysis", analysisId));
                setModalState({ message: "Analysis deleted."});
            },
            onCancel: () => setModalState({message: ''})
        });
    };

    const handleSave = async (childId, dataToSave) => {
        const analysisRef = doc(db, "competitorAnalysis", childId);
        await setDoc(analysisRef, {...dataToSave, userId: user.uid, lastUpdatedAt: new Date()}, { merge: true });
        setIsEditorOpen(false);
        setIsAddModalOpen(false);
        setModalState({ message: "Analysis saved successfully!"});
    };

    const formatDate = (date) => date ? date.toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }) : 'N/A';

    return (
        <div>
            <MessageModal message={modalState.message} onConfirm={() => setModalState({message:''})} showCancel={modalState.showCancel} onCancel={() => setModalState({message: ''})} />
            {isAddModalOpen && <AddAnalysisModal onSave={handleSave} onClose={() => setIsAddModalOpen(false)} user={user}/>}
            {isViewerOpen && <AnalysisViewer analysis={selectedAnalysis} productDetails={productDetails} onClose={() => setIsViewerOpen(false)} />}

            <div className="page-header">
                <h2>Competitor Analysis Records</h2>
                <div>
                    <button className="submit-button" onClick={() => onNavigateToAddAnalysis()}>+ Add New Analysis</button>
                    <button className="cancel-button" style={{marginLeft: '1rem'}} onClick={() => setView('companyDashboard')}>Back</button>
                </div>
            </div>

            <div className="search-filter-bar-container" style={{marginBottom: '1.5rem'}}>
                <SearchAndFilterBar 
                    searchTerm={searchTerm}
                    setSearchTerm={setSearchTerm}
                    filters={{}}
                    setFilters={() => {}}
                />
            </div>
            
            <div className="table-wrapper">
                <table className="product-list-table">
                    <thead><tr><th>Child Product ID</th><th>Item Name</th><th>Competitor 1</th><th>Comp. 1 Price (RM)</th><th>Last Updated</th><th className="cell-actions">Actions</th></tr></thead>
                    <tbody>
                        {loading && <tr><td colSpan="6" style={{textAlign: 'center'}}>Loading...</td></tr>}
                        {!loading && filteredAnalyses.length === 0 && (<tr><td colSpan="6" style={{textAlign: 'center'}}>No analyses found.</td></tr>)}
                        {filteredAnalyses.map(analysis => {
                            const product = inventoryMap.get(analysis.childId);
                            return (
                                <tr key={analysis.id}>
                                    <td>{analysis.childId}</td>
                                    <td>{product ? product.itemName : 'N/A'}</td>
                                    <td>{analysis.comp1_company || 'N/A'}</td>
                                    <td>{Number(analysis.comp1_price || 0).toFixed(2)}</td>
                                    <td>{formatDate(analysis.lastUpdatedAt)}</td>
                                    <td className="cell-actions">
                                        <button className="view-button" onClick={() => handleView(analysis)}>View</button>
                                        <button className="edit-button" onClick={() => handleEdit(analysis)}>Edit</button>
                                        <button className="delete-button" onClick={() => handleDelete(analysis.id)}>Delete</button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {isEditorOpen && (
                <AnalysisEditor analysis={selectedAnalysis} productDetails={productDetails} onSave={handleSave} onCancel={() => setIsEditorOpen(false)}/>
            )}
        </div>
    );
};

export default CompetitorsPage;